import type { AppID } from '../../cross-app-shared/types/db';
import {
  type AppAccessEffective,
  type AppAccessLevel,
  type AppAccessOverview,
  type AppAccessSettings,
  type AppAccessTarget,
  appAccessResolve,
  appAccessTargetKey,
} from '../../cross-app-shared/services/app-access';

export interface AppAccessNode {
  key: string;
  target: AppAccessTarget;
  name: string;
  /** e.g. "Facility #12", used for display and so admins can search by ID. */
  idLabel: string;
  settings: AppAccessSettings;
  parent: AppAccessNode | null;
  children: AppAccessNode[];
}

/** Returns the explicit setting at a node for one app, or null to inherit. */
export type AppAccessSettingOf = (node: AppAccessNode) => boolean | null;

export function appAccessSettingOfCurrent(appID: AppID): AppAccessSettingOf {
  return (node) => node.settings[appID]?.isGrantedAccess ?? null;
}

export function appAccessTreeBuild(overview: AppAccessOverview): AppAccessNode[] {
  const tenants = new Map<number, AppAccessNode>();
  const groups = new Map<number, AppAccessNode>();

  for (const tenant of overview.tenants) {
    tenants.set(tenant.tenantID, {
      key: appAccessTargetKey({ level: 'tenant', tenantID: tenant.tenantID }),
      target: { level: 'tenant', tenantID: tenant.tenantID },
      name: tenant.isDemo ? `${tenant.name} (Demo)` : tenant.name,
      idLabel: `Tenant #${tenant.tenantID}`,
      settings: tenant.settings,
      parent: null,
      children: [],
    });
  }

  for (const group of overview.groups) {
    const parent = tenants.get(group.tenantID);
    if (!parent) {
      continue;
    }

    const node: AppAccessNode = {
      key: appAccessTargetKey({ level: 'group', facilityGroupID: group.facilityGroupID }),
      target: { level: 'group', facilityGroupID: group.facilityGroupID },
      name: group.name,
      idLabel: `Group #${group.facilityGroupID}`,
      settings: group.settings,
      parent,
      children: [],
    };
    groups.set(group.facilityGroupID, node);
    parent.children.push(node);
  }

  for (const facility of overview.facilities) {
    const parent = groups.get(facility.facilityGroupID);
    if (!parent) {
      continue;
    }

    parent.children.push({
      key: appAccessTargetKey({ level: 'facility', facilityID: facility.facilityID }),
      target: { level: 'facility', facilityID: facility.facilityID },
      name: facility.name,
      idLabel: `Facility #${facility.facilityID}`,
      settings: facility.settings,
      parent,
      children: [],
    });
  }

  return Array.from(tenants.values());
}

/** The node and its ancestors, ordered from the tenant down to the node itself. */
export function appAccessChain(node: AppAccessNode): AppAccessNode[] {
  const result: AppAccessNode[] = [];
  for (let current: AppAccessNode | null = node; current; current = current.parent) {
    result.unshift(current);
  }

  return result;
}

export function appAccessEffective(node: AppAccessNode, settingOf: AppAccessSettingOf): AppAccessEffective {
  return appAccessResolve(appAccessChain(node).map((n) => ({ level: n.target.level, setting: settingOf(n) })));
}

/**
 * Counts how many facilities at or beneath a node effectively have access. Groups and tenants don't use apps
 * themselves, so this is what actually matters to users.
 */
export function appAccessFacilityCount(
  node: AppAccessNode,
  settingOf: AppAccessSettingOf,
): { granted: number; total: number } {
  const inherited = node.parent ? appAccessEffective(node.parent, settingOf).isGrantedAccess : false;

  const walk = (n: AppAccessNode, parentGranted: boolean): { granted: number; total: number } => {
    const granted = settingOf(n) ?? parentGranted;
    if (n.target.level === 'facility') {
      return { granted: granted ? 1 : 0, total: 1 };
    }

    const result = { granted: 0, total: 0 };
    for (const child of n.children) {
      const counts = walk(child, granted);
      result.granted += counts.granted;
      result.total += counts.total;
    }

    return result;
  };

  return walk(node, inherited);
}

/** Nodes strictly beneath `node` with an explicit setting, skipping anything in `excludeKeys`. */
export function appAccessOverridesBeneath(
  node: AppAccessNode,
  settingOf: AppAccessSettingOf,
  excludeKeys: Set<string>,
): AppAccessNode[] {
  const result: AppAccessNode[] = [];
  const walk = (n: AppAccessNode) => {
    for (const child of n.children) {
      if (!excludeKeys.has(child.key) && settingOf(child) !== null) {
        result.push(child);
      }
      walk(child);
    }
  };
  walk(node);

  return result;
}

/**
 * The settings as they will be after saving a set of staged changes along a chain, optionally clearing every other
 * explicit setting beneath some of the nodes on that chain. Mirrors what `appAccessUpdate` does on the backend.
 */
export function appAccessSettingOfStaged(
  current: AppAccessSettingOf,
  staged: Map<string, boolean | null>,
  overridesBeneathClear: Set<string>,
): AppAccessSettingOf {
  return (node) => {
    const stagedValue = staged.get(node.key);
    if (stagedValue !== undefined) {
      return stagedValue;
    }

    for (let ancestor = node.parent; ancestor; ancestor = ancestor.parent) {
      if (overridesBeneathClear.has(ancestor.key)) {
        return null;
      }
    }

    return current(node);
  };
}

export interface AppAccessRow {
  node: AppAccessNode;
  depth: number;
  isExpanded: boolean;
  /** Whether this row matched the search itself, as opposed to being shown for context. */
  isSearchMatch: boolean;
}

function appAccessNodeMatches(node: AppAccessNode, query: string): boolean {
  return node.name.toLowerCase().includes(query) || node.idLabel.toLowerCase().includes(query);
}

/**
 * Flattens the tree into the rows to display.
 *
 * Without a search, children are shown only beneath expanded nodes. With a search, a row is shown when it matches,
 * when an ancestor matches (so searching for a tenant still lets you drill into it), or when it leads to a match. Nodes
 * leading to a match are expanded automatically.
 */
export function appAccessRowsVisible(roots: AppAccessNode[], expanded: Set<string>, search: string): AppAccessRow[] {
  const query = search.trim().toLowerCase();
  const rows: AppAccessRow[] = [];

  const descendantMatches = new Map<string, boolean>();
  const computeDescendantMatches = (node: AppAccessNode): boolean => {
    let result = false;
    for (const child of node.children) {
      // Visit every child so the whole map gets filled in.
      const childMatches = computeDescendantMatches(child);
      result = childMatches || appAccessNodeMatches(child, query) || result;
    }
    descendantMatches.set(node.key, result);
    return result;
  };
  if (query) {
    roots.forEach(computeDescendantMatches);
  }

  const walk = (node: AppAccessNode, depth: number, ancestorMatched: boolean) => {
    const isSearchMatch = query !== '' && appAccessNodeMatches(node, query);
    const leadsToMatch = descendantMatches.get(node.key) ?? false;
    if (query && !isSearchMatch && !ancestorMatched && !leadsToMatch) {
      return;
    }

    const isExpanded = node.children.length > 0 && (expanded.has(node.key) || leadsToMatch);
    rows.push({ node, depth, isExpanded, isSearchMatch });

    if (isExpanded) {
      for (const child of node.children) {
        walk(child, depth + 1, ancestorMatched || isSearchMatch);
      }
    }
  };
  for (const root of roots) {
    walk(root, 0, false);
  }

  return rows;
}

export const appAccessLevelLabels: Record<AppAccessLevel, string> = {
  tenant: 'Tenant',
  group: 'Group',
  facility: 'Facility',
};
